import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { BaseForm } from 'components/common/forms/BaseForm/BaseForm';
import notificationHook from 'hooks/notificationHook';
import { useAppDispatch } from 'hooks/reduxHooks';
import { doSetNewPassword } from 'store/slices/authSlice';
import { RuleObject } from 'antd/es/form';
import { NamePath, StoreValue } from 'antd/es/form/interface';
import { authPaths } from 'constants/routePaths';
import { ApiError } from 'types';
import statusMessages from 'statusMessages';
import { InputPassword } from 'components/common/inputs/InputPassword/InputPassword';
import { Button } from 'antd';
import 'styles/sass/new-password-form.scss';

interface NewPasswordFormData {
  password: string;
  confirmPassword: string;
}

const initStates = {
  password: '',
  confirmPassword: '',
};

export const NewPasswordForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isLoading, setLoading] = useState(false);

  const handleSubmit = (values: NewPasswordFormData) => {
    setLoading(true);
    dispatch(doSetNewPassword({ newPassword: values.password }))
      .unwrap()
      .then(() => {
        navigate(`${authPaths.basePath}/${authPaths.login}`);
        notificationHook({
          type: 'success',
          message: `La contraseña ha sido actualizada`,
          description: `Ingresa con tu nueva contraseña`,
        });
      })
      .catch((error) => {
        const currentStatus = (error as ApiError).statusCode;
        notificationHook({
          type: 'error',
          message: statusMessages.auth[
            currentStatus ? currentStatus : 500
          ] as string,
        });
        setLoading(false);
      });
  };

  return (
    <div className="form-wrapper">
      <BaseForm
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark="optional"
        initialValues={initStates}
      >
        <h1 className="title">Recuperar contraseña</h1>
        <div className="description">Ingresa una nueva contraseña</div>
        <BaseForm.Item
          className="form-item"
          name="password"
          label={`Contraseña`}
          rules={[
            { required: true, message: `Nueva contraseña` },
            {
              min: 6,
              message: 'La contraseña debe tener al menos 6 caracteres',
            },
          ]}
        >
          <InputPassword
            className="form-input-password"
            placeholder={`Contraseña`}
          />
        </BaseForm.Item>
        <BaseForm.Item
          className="form-item"
          name="confirmPassword"
          label={`Confirmar contraseña`}
          dependencies={['password']}
          rules={[
            { required: true, message: `Campo requerido` },
            {
              min: 6,
              message: 'La contraseña debe tener al menos 6 caracteres',
            },
            ({
              getFieldValue,
            }: {
              getFieldValue: (name: NamePath) => StoreValue;
            }) => ({
              validator(_: RuleObject, value: string) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(`Las Contraseñas no coinciden`)
                );
              },
            }),
          ]}
          hasFeedback
        >
          <InputPassword
            className="form-input-password"
            placeholder={`Confirmar contraseña`}
          />
        </BaseForm.Item>
        <BaseForm.Item noStyle>
          <Button
            className="submit-button ant-btn ant-btn-primary"
            type="primary"
            htmlType="submit"
            loading={isLoading}
          >
            {`Resetear contraseña`}
          </Button>
        </BaseForm.Item>
      </BaseForm>
    </div>
  );
};
